'use client'
import React, { useState } from 'react'
import { useBillingCycleTotal } from '@/src/queries/earnings/new/billingCycleTotal'
import {
  Box,
  Button,
  Flex,
  Icon,
  Image,
  Link,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { FaArrowRight } from 'react-icons/fa6'
import VideoUploadFlow from '@/components/upload/videouploadflow'
import { useUser } from '@/src/store/user-store'
import { useRouter } from 'next/navigation'
import TableContent from './table-content'
import CreatorAdvancesContent from './creator-advances-content'
import EarningAds from '../../modals/earning-on-ads'
import CombineMoreInfo from '../../modals/combine-more-info'
import { formatLykCoinValue, formatLykCoins } from '@/lib/lyk-coin'
import { useAdvanceRecoveryTimelineQuery } from '@/src/queries/earnings/advance-recovery-timeline'

type CreatorSubTab = 'content_earnings' | 'advances'

const EmptyCreator = () => {
  const router = useRouter()
  const { user } = useUser()
  const { isOpen, onOpen, onClose } = useDisclosure()
  return (
    <>
      <VideoUploadFlow onOpen={onOpen} onClose={onClose} isOpen={isOpen} />
      <Flex className="my-2 w-full flex-col items-center justify-center gap-2">
        <Image
          src={'/rewards/breakdown/video.svg'}
          alt="Icon"
          w={{ base: 10, sm: 12 }}
          h={{ base: 10, sm: 12 }}
          mb={1}
        />

        <Text
          fontWeight={600}
          color={'#727272'}
          my={1}
          fontSize={{ base: 16, sm: 18 }}
          textAlign={'center'}
        >
          Create videos on LYKSTAGE to start earning
        </Text>

        <Button
          width={'full'}
          size={'md'}
          bg={'lyk-green'}
          color={'white'}
          fontSize={{ base: 14, sm: 16 }}
          as={Link}
          onClick={() => {
            if (user !== null) {
              onOpen()
            } else {
              router.push('/auth/login')
            }
          }}
          _hover={{ opacity: 0.7, textDecoration: 'none' }}
          textDecoration={'none'}
          rightIcon={<Icon as={FaArrowRight} />}
        >
          Upload first video
        </Button>
      </Flex>
    </>
  )
}

const EmptyViewer = () => {
  return (
    <Flex className="my-2 w-full flex-col items-center justify-center gap-2">
      <Image
        src={'/rewards/video.svg'}
        alt="Icon"
        w={{ base: 10, sm: 12 }}
        h={{ base: 10, sm: 12 }}
        mb={1}
      />

      <Text
        fontWeight={600}
        color={'#727272'}
        my={1}
        fontSize={{ base: 16, sm: 18 }}
        textAlign={'center'}
        lineHeight={'normal'}
      >
        Watch videos on LYKSTAGE to start earning
      </Text>

      <Button
        width={'full'}
        size={'md'}
        bg={'lyk-green'}
        color={'white'}
        fontSize={{ base: 14, sm: 16 }}
        as={Link}
        href={'/home'}
        _hover={{ opacity: 0.7, textDecoration: 'none' }}
      >
        Go to Homefeed
      </Button>
    </Flex>
  )
}

const RecentActivity = () => {
  const currentBillingTotal = useBillingCycleTotal()
  const [isEarningAdOpen, setIsEarningAdOpen] = useState(false)
  const [isCombineMoreInfoOpen, setCombineMoreInfoOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('other_earnings')
  const [creatorSubTab, setCreatorSubTab] =
    useState<CreatorSubTab>('content_earnings')
  const {
    data: advanceRecoveryTimeline,
    error: advanceRecoveryError,
    isLoading: isAdvanceRecoveryLoading,
    isError: isAdvanceRecoveryError,
  } = useAdvanceRecoveryTimelineQuery(
    activeTab === 'creator_earnings'
  )
  const isAdvanceNotEnrolled =
    advanceRecoveryError instanceof Error &&
    advanceRecoveryError.message === 'error/not-enrolled-in-advance'
  const hasCreatorEarnings =
    Boolean(currentBillingTotal.data?.creatorEarn?.lykCoin) ||
    !isAdvanceNotEnrolled

  const formatWatchTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const remainingSeconds = seconds % 60

    return `${hours < 10 ? '0' : ''}${hours}:${
      minutes < 10 ? '0' : ''
    }${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`
  }

  const handleCombineMoreInfoOpen = () => {
    setCombineMoreInfoOpen(true)
  }

  const handleTabChange = (index: number) => {
    const tabNames = ['other_earnings', 'creator_earnings', 'viewer_earnings']
    const newTab = tabNames[index]
    setActiveTab(newTab)
  }

  return (
    <>
      <CombineMoreInfo
        isOpen={isCombineMoreInfoOpen}
        onClose={() => setCombineMoreInfoOpen(false)}
      />
      <EarningAds
        isOpen={isEarningAdOpen}
        onClose={() => setIsEarningAdOpen(false)}
      />
      <Box className="rounded-2xl border-2 border-[#DDDDDD]">
        <Tabs onChange={handleTabChange}>
          <TabList className="rounded-t-2xl bg-[#E9E9E970] font-semibold text-[#656565]">
            <Tab
              flex={1}
              justifyContent="center"
              textAlign="center"
              px={{ base: 2, sm: 4 }}
              py={{ base: 3, sm: 4 }}
              fontSize={{ base: 13, sm: 15 }}
              lineHeight={1.2}
              _selected={{
                borderBottom: '3px solid #24BFA3',
                bg: 'transparent',
              }}
            >
              Other Earnings
            </Tab>
            <Tab
              flex={1}
              justifyContent="center"
              textAlign="center"
              px={{ base: 2, sm: 4 }}
              py={{ base: 3, sm: 4 }}
              fontSize={{ base: 13, sm: 15 }}
              lineHeight={1.2}
              _selected={{
                borderBottom: '3px solid #24BFA3',
                bg: 'transparent',
              }}
            >
              Creator Earnings
            </Tab>
            <Tab
              flex={1}
              justifyContent="center"
              textAlign="center"
              px={{ base: 2, sm: 4 }}
              py={{ base: 3, sm: 4 }}
              fontSize={{ base: 13, sm: 15 }}
              lineHeight={1.2}
              _selected={{
                borderBottom: '3px solid #24BFA3',
                bg: 'transparent',
              }}
            >
              Viewer Earnings
            </Tab>
          </TabList>

          <TabPanels p={{ base: 0, sm: 2 }}>
            {/* Other Activity Panel */}
            <TabPanel>
              {currentBillingTotal.data?.other?.lykCoin && (
                <>
                  <Flex className="flex-wrap gap-0 sm:flex-nowrap sm:gap-6">
                    <Flex className="w-full flex-col">
                      <Box
                        bg={{
                          base: 'none',
                          sm: 'linear-gradient(52.62deg, rgba(255, 255, 255, 0.2) 12.76%, rgba(36, 191, 163, 0.2) 110.86%)',
                        }}
                        boxShadow={{
                          base: 'none',
                          sm: '0px 5px 9px -2px rgba(36, 191, 163, 0.40)',
                        }}
                        className="relative rounded-xl bg-cover bg-no-repeat px-3 pb-0 pt-3 text-white sm:p-5"
                      >
                        <Flex
                          alignItems={'center'}
                          justifyContent={{
                            base: 'center',
                            sm: 'space-between',
                          }}
                          fontSize={{ base: 14, sm: 18 }}
                        >
                          <Text
                            fontWeight={{ base: 700, sm: 600 }}
                            color={'#727272'}
                            textAlign={{ base: 'center', sm: 'left' }}
                            lineHeight={{ base: 1.35, sm: 'normal' }}
                            maxW={{ base: '290px', sm: 'none' }}
                          >
                            Other Earnings (Bonuses, Rewards, etc.)
                          </Text>
                          <Flex
                            color={'lyk-green'}
                            display={{ base: 'none', sm: 'flex' }}
                            className="items-center font-semibold"
                          >
                            More info{' '}
                            <Image
                              boxSize={{ base: 4, sm: 5 }}
                              src={'/rewards/breakdown/info-green.svg'}
                              alt="Icon"
                              cursor={'pointer'}
                              marginLeft={'4px'}
                              onClick={handleCombineMoreInfoOpen}
                            />
                          </Flex>
                        </Flex>

                        <hr className="my-4 hidden font-semibold text-[#DDDDDD] sm:block" />

                        <Text
                          textAlign={'center'}
                          fontSize={{ base: 14, sm: 14, md: 15 }}
                          fontWeight={{ base: 700, sm: 600 }}
                          color={'#727272'}
                          lineHeight={{ base: 1.35, sm: 'normal' }}
                        >
                          Your other earnings balance
                        </Text>

                        <Flex
                          mb={3}
                          mt={1}
                          alignItems="center"
                          gap={2.5}
                          color="lyk-green"
                        >
                          <Flex
                            color="#3E3E3E"
                            className="w-full items-center justify-between gap-2"
                            fontWeight={600}
                          >
                            <Flex className="flex-col text-center text-[#797979]">
                              <Text fontSize={{ base: 22, sm: 24, md: 26 }}>
                                {formatLykCoins(currentBillingTotal.data?.other?.lykCoin) || '0'}
                              </Text>
                              <Flex
                                alignItems={'center'}
                                justifyContent={'center'}
                                gap={1}
                              >
                                <Image
                                  boxSize={{ base: 4, sm: 7 }}
                                  src="/logo-color-circle.svg"
                                  alt="Lykstage"
                                />
                                <Text
                                  fontSize={{ base: 15, sm: 18, md: 20 }}
                                  className="text-green sm:text-[#797979]"
                                >
                                  LYK Coins
                                </Text>
                              </Flex>
                            </Flex>

                            <Image
                              src="/rewards/compare.svg"
                              alt="compare"
                              boxSize={{ base: 3.5, sm: 5 }}
                            />

                            <Flex className="flex-col text-center text-[#797979]">
                              <Text fontSize={{ base: 22, sm: 24, md: 26 }}>
                                {formatLykCoinValue(currentBillingTotal.data?.other?.lykCoin || '0.00')}
                              </Text>
                              <Flex justifyContent={'center'}>
                                <Text
                                  fontSize={{ base: 15, sm: 18, md: 20 }}
                                  className="text-green sm:text-[#797979]"
                                >
                                  US$
                                </Text>
                              </Flex>
                            </Flex>
                          </Flex>
                        </Flex>
                      </Box>
                    </Flex>
                  </Flex>

                  <Text
                    className="mb-4 mt-2 text-center font-medium text-[#9F9F9F] sm:my-6"
                    fontSize={{ base: 14, sm: 16, md: 18 }}
                    lineHeight={{ base: 1.35, sm: 'normal' }}
                  >
                    Recent earnings (in LYK Coins) from bonuses
                  </Text>

                  <TableContent type="bonus" />
                  <TableContent type="oldEarnings" />
                  {/* keep remmber to open this comment when we r going to give win lykcoins <TableContent type="others_earning" /> */}

                  <Flex className="justify-end">
                    <Button
                      className="mt-4 font-medium no-underline max-sm:w-full"
                      py={5}
                      px={10}
                      fontSize={{ base: 14, sm: 16 }}
                      bg="lyk-green"
                      color={'white'}
                      as={Link}
                      href="/earnings/breakdown"
                      _hover={{ opacity: 0.7, textDecoration: 'none' }}
                      textDecoration={'none'}
                    >
                      Watch Time Earnings Breakdown
                    </Button>
                  </Flex>
                </>
              )}
            </TabPanel>

            {/* Creator Panel */}
            <TabPanel>
              {!hasCreatorEarnings ? (
                <EmptyCreator />
              ) : (
                <>
                  <Flex className="flex-wrap gap-0 sm:flex-nowrap sm:gap-6">
                    <Flex className="w-full flex-col">
                      <Box
                        bg={{
                          base: 'none',
                          sm: 'linear-gradient(52.62deg, rgba(255, 255, 255, 0.2) 12.76%, rgba(36, 191, 163, 0.2) 110.86%)',
                        }}
                        boxShadow={{
                          base: 'none',
                          sm: '0px 5px 9px -2px rgba(36, 191, 163, 0.40)',
                        }}
                        className="relative rounded-xl bg-cover bg-no-repeat px-3 pb-0 pt-3 text-white sm:p-5"
                      >
                        <Flex
                          alignItems={'center'}
                          justifyContent={{
                            base: 'center',
                            sm: 'space-between',
                          }}
                          fontSize={{ base: 14, sm: 18 }}
                        >
                          <Text
                            fontWeight={{ base: 700, sm: 600 }}
                            color={'#727272'}
                            textAlign={{ base: 'center', sm: 'left' }}
                            lineHeight={{ base: 1.35, sm: 'normal' }}
                            maxW={{ base: '290px', sm: 'none' }}
                          >
                            {/* Earnings as a Creator */}
                            Watch Time Earnings (in LYK Coins) as a Creator
                          </Text>
                          <Flex
                            color={'lyk-green'}
                            display={{ base: 'none', sm: 'flex' }}
                            className="items-center font-semibold"
                          >
                            More info{' '}
                            <Image
                              boxSize={{ base: 4, sm: 5 }}
                              src={'/rewards/breakdown/info-green.svg'}
                              alt="Icon"
                              marginLeft={'4px'}
                              cursor={'pointer'}
                              onClick={handleCombineMoreInfoOpen}
                            />
                          </Flex>
                        </Flex>

                        <hr className="my-4 hidden font-semibold text-[#DDDDDD] sm:block" />

                        <Text
                          textAlign={'center'}
                          fontSize={{ base: 14, sm: 14, md: 15 }}
                          fontWeight={{ base: 700, sm: 600 }}
                          color={'#727272'}
                          lineHeight={{ base: 1.35, sm: 'normal' }}
                        >
                          Your estimated total creator earnings
                        </Text>

                        <Flex
                          mb={3}
                          mt={1}
                          alignItems="center"
                          gap={2.5}
                          color="lyk-green"
                        >
                          <Flex
                            color="#3E3E3E"
                            className="w-full items-center justify-between gap-2"
                            fontWeight={600}
                          >
                            <Flex className="flex-col text-center text-[#797979]">
                              <Text fontSize={{ base: 22, sm: 24, md: 26 }}>
                                {formatLykCoins(currentBillingTotal.data?.creatorEarn?.lykCoin) || '0'}  
                              </Text>
                              <Flex
                                alignItems={'center'}
                                justifyContent={'center'}
                                gap={1}
                              >
                                <Image
                                  boxSize={{ base: 4, sm: 7 }}
                                  src="/logo-color-circle.svg"
                                  alt="Lykstage"
                                />
                                <Text
                                  fontSize={{ base: 15, sm: 18, md: 20 }}
                                  className="text-green sm:text-[#797979]"
                                >
                                  LYK Coins
                                </Text>
                              </Flex>
                            </Flex>

                            <Image
                              src="/rewards/compare.svg"
                              alt="compare"
                              boxSize={{ base: 3.5, sm: 5 }}
                            />

                            <Flex className="flex-col text-center text-[#797979]">
                              <Text fontSize={{ base: 22, sm: 24, md: 26 }}>
                                {formatLykCoinValue(currentBillingTotal.data?.creatorEarn?.lykCoin || '0.00')}  
                              </Text>
                              <Flex justifyContent={'center'}>
                                <Text
                                  fontSize={{ base: 15, sm: 18, md: 20 }}
                                  className="text-green sm:text-[#797979]"
                                >
                                  US$
                                </Text>
                              </Flex>
                            </Flex>
                          </Flex>
                        </Flex>
                      </Box>
                    </Flex>

                    {/* <Box
                      boxShadow={{
                        base: 'none',
                        sm: '0px 5px 9px -2px rgba(36, 191, 163, 0.40)',
                      }}
                      className="relative w-full rounded-xl bg-[#FCFCFC] bg-cover bg-no-repeat px-3 pb-0 pt-3 text-white sm:w-[70%] sm:p-5 md:w-[78%]"
                    >
                      <Flex justifyContent={'center'} alignItems={'center'}>
                        <Text
                          fontWeight={{ base: 700, sm: 600 }}
                          fontSize={{ base: 16, sm: 18 }}
                          color={'#727272'}
                        >
                          Total watchtime on your videos
                        </Text>
                      </Flex>

                      <hr className="my-4 hidden font-semibold text-[#DDDDDD] sm:block" />

                      <Flex
                        mb={3}
                        mt={1}
                        justifyContent="center"
                        gap={2.5}
                        color="lyk-green"
                      >
                        {currentBillingTotal.isPending ? (
                          <Skeleton
                            height="50px"
                            my={3}
                            width="100px"
                          ></Skeleton>
                        ) : (
                          <>
                            <Flex className="flex-col items-center font-medium">
                              <Text
                                fontSize={{ base: 26, sm: 24 }}
                                className="text-[#797979]"
                              >
                                {formatWatchTime(
                                  currentBillingTotal.data?.creatorEarn
                                    ?.watchtime
                                )}
                              </Text>
                              <Flex justifyContent={'center'}>
                                <Text
                                  fontSize={20}
                                  display={{ base: 'none', sm: 'block' }}
                                  className="text-[#9F9F9F]"
                                >
                                  Watched
                                </Text>
                              </Flex>
                            </Flex>
                          </>
                        )}
                      </Flex>
                    </Box> */}
                                  {/* Hours */}
                  </Flex>

                  <Text
                    className="mb-4 mt-2 text-center font-medium text-[#9F9F9F] sm:my-6"
                    fontSize={{ base: 14, sm: 16, md: 18 }}
                    lineHeight={{ base: 1.35, sm: 'normal' }}
                  >
                    {isAdvanceNotEnrolled
                      ? 'Recent Earnings from your videos'
                      : creatorSubTab === 'content_earnings'
                      ? 'Recent Earnings from your videos'
                      : 'Recent advance payments'}
                  </Text>

                  {isAdvanceRecoveryLoading ? (
                    <></>
                  ) : (
                    <Tabs
                      index={
                        isAdvanceNotEnrolled || creatorSubTab === 'content_earnings'
                          ? 0
                          : 1
                      }
                      onChange={(index) =>
                        setCreatorSubTab(
                          index === 0 ? 'content_earnings' : 'advances'
                        )
                      }
                      variant="unstyled"
                    >
                      <TabList className="mb-4 rounded-t-2xl bg-[#E9E9E970] font-semibold text-[#656565]">
                        <Tab
                          flex={1}
                          justifyContent="center"
                          textAlign="center"
                          px={{ base: 3, sm: 4 }}
                          py={{ base: 3, sm: 4 }}
                          fontSize={{ base: 13, sm: 15 }}
                          lineHeight={1.2}
                          _selected={{
                            borderBottom: '3px solid #24BFA3',
                            color: '#24BFA3',
                          }}
                        >
                          Content Earnings
                        </Tab>
                        {!isAdvanceNotEnrolled && (
                          <Tab
                            flex={1}
                            justifyContent="center"
                            textAlign="center"
                            px={{ base: 3, sm: 4 }}
                            py={{ base: 3, sm: 4 }}
                            fontSize={{ base: 13, sm: 15 }}
                            lineHeight={1.2}
                            _selected={{
                              borderBottom: '3px solid #24BFA3',
                              color: '#24BFA3',
                            }}
                          >
                            Advances
                          </Tab>
                        )}
                      </TabList>

                      <TabPanels>
                        <TabPanel px={0}>
                          <TableContent type="creator" />

                          <Flex className="justify-end">
                            <Button
                              className="no-underline max-sm:w-full"
                              py={5}
                              px={10}
                              fontSize={{ base: 14, sm: 16 }}
                              bg="lyk-green"
                              as={Link}
                              href="/creator-analytics"
                              color="white"
                              fontWeight={{ base: 400, sm: 500 }}
                              textDecoration={'none'}
                              _hover={{ opacity: 0.7, textDecoration: 'none' }}
                            >
                              Creator analytics
                            </Button>
                        </Flex>
                      </TabPanel>

                      {!isAdvanceNotEnrolled && (
                        <TabPanel px={0}>
                          <CreatorAdvancesContent
                            advanceRecoveryTimeline={advanceRecoveryTimeline}
                            isLoading={isAdvanceRecoveryLoading}
                            isError={isAdvanceRecoveryError}
                          />
                        </TabPanel>
                      )}
                    </TabPanels>
                  </Tabs>
                  )}
                </>
              )}
            </TabPanel>

            {/* Viewer Panel */}
            <TabPanel>
              {!currentBillingTotal.data?.viewEarn?.lykCoin ? (
                <EmptyViewer />
              ) : (
                <>
                  <Flex className="flex-wrap gap-0 sm:flex-nowrap sm:gap-6">
                    <Flex className="w-full flex-col">
                      <Box
                        bg={{
                          base: 'none',
                          sm: 'linear-gradient(52.62deg, rgba(255, 255, 255, 0.2) 12.76%, rgba(36, 191, 163, 0.2) 110.86%)',
                        }}
                        boxShadow={{
                          base: 'none',
                          sm: '0px 5px 9px -2px rgba(36, 191, 163, 0.40)',
                        }}
                        className="relative rounded-xl bg-cover bg-no-repeat px-3 pb-0 pt-3 text-white sm:p-5"
                      >
                        <Flex
                          alignItems={'center'}
                          justifyContent={{
                            base: 'center',
                            sm: 'space-between',
                          }}
                          fontSize={{ base: 14, sm: 18 }}
                        >
                          <Text
                            fontWeight={{ base: 700, sm: 600 }}
                            color={'#727272'}
                            textAlign={{ base: 'center', sm: 'left' }}
                            lineHeight={{ base: 1.35, sm: 'normal' }}
                            maxW={{ base: '290px', sm: 'none' }}
                          >
                            Watch Time Earnings (in LYK Coins) as a Viewer
                          </Text>
                          <Flex
                            color={'lyk-green'}
                            display={{ base: 'none', sm: 'flex' }}
                            className="items-center font-semibold"
                          >
                            More info{' '}
                            <Image
                              boxSize={{ base: 4, sm: 5 }}
                              src={'/rewards/breakdown/info-green.svg'}
                              alt="Icon"
                              cursor={'pointer'}
                              marginLeft={'4px'}
                              onClick={handleCombineMoreInfoOpen}
                            />
                          </Flex>
                        </Flex>

                        <hr className="my-4 hidden font-semibold text-[#DDDDDD] sm:block" />

                        <Text
                          textAlign={'center'}
                          fontSize={{ base: 14, sm: 14, md: 15 }}
                          fontWeight={{ base: 700, sm: 600 }}
                          color={'#727272'}
                          lineHeight={{ base: 1.35, sm: 'normal' }}
                        >
                          Your estimated viewer earnings balance
                        </Text>

                        <Flex
                          mb={3}
                          mt={1}
                          alignItems="center"
                          gap={2.5}
                          color="lyk-green"
                        >
                          <Flex
                            color="#3E3E3E"
                            className="w-full items-center justify-between gap-2"
                            fontWeight={600}
                          >
                            <Flex className="flex-col text-center text-[#797979]">
                              <Text fontSize={{ base: 22, sm: 24, md: 26 }}>
                                {formatLykCoins(currentBillingTotal.data?.viewEarn?.lykCoin) || '0'}
                              </Text>
                              <Flex
                                alignItems={'center'}
                                justifyContent={'center'}
                                gap={1}
                              >
                                <Image
                                  boxSize={{ base: 4, sm: 7 }}
                                  src="/logo-color-circle.svg"
                                  alt="Lykstage"
                                />
                                <Text
                                  fontSize={{ base: 15, sm: 18, md: 20 }}
                                  className="text-green sm:text-[#797979]"
                                >
                                  LYK Coins
                                </Text>
                              </Flex>
                            </Flex>

                            <Image
                              src="/rewards/compare.svg"
                              alt="compare"
                              boxSize={{ base: 3.5, sm: 5 }}
                            />

                            <Flex className="flex-col text-center text-[#797979]">
                              <Text fontSize={{ base: 22, sm: 24, md: 26 }}>
                                {formatLykCoinValue(currentBillingTotal.data?.viewEarn?.lykCoin) || '0.00'}
                              </Text>
                              <Flex justifyContent={'center'}>
                                <Text
                                  fontSize={{ base: 15, sm: 18, md: 20 }}
                                  className="text-green sm:text-[#797979]"
                                >
                                  US$
                                </Text>
                              </Flex>
                            </Flex>
                          </Flex>
                        </Flex>
                      </Box>
                    </Flex>

                    {/* <Box
                      boxShadow={{
                        base: 'none',
                        sm: '0px 5px 9px -2px rgba(36, 191, 163, 0.40)',
                      }}
                      className="relative w-full rounded-xl bg-[#FCFCFC] bg-cover bg-no-repeat px-3 pb-0 pt-3 text-white sm:w-[41%] sm:p-5 md:w-[62%]"
                    >
                      <Flex justifyContent={'center'} alignItems={'center'}>
                        <Text
                          fontWeight={{ base: 700, sm: 600 }}
                          fontSize={{ base: 15, sm: 18 }}
                          color={'#727272'}
                        >
                          Your total watchtime
                        </Text>
                      </Flex>

                      <hr className="my-4 hidden font-semibold text-[#DDDDDD] sm:block" />

                      <Flex
                        mb={3}
                        mt={1}
                        justifyContent="center"
                        gap={2.5}
                        color="lyk-green"
                      >
                        {currentBillingTotal.isPending ? (
                          <Skeleton
                            height="50px"
                            my={3}
                            width="100px"
                          ></Skeleton>
                        ) : (
                          <>
                            <Flex className="flex-col items-center font-medium">
                              <Text
                                fontSize={{ base: 26, sm: 24 }}
                                className="text-[#797979]"
                              >
                                {formatWatchTime(
                                  currentBillingTotal.data?.viewEarn?.watchtime
                                )}
                              </Text>
                              <Flex justifyContent={'center'}>
                                <Text
                                  fontSize={20}
                                  display={{ base: 'none', sm: 'block' }}
                                  className="text-[#9F9F9F]"
                                >
                                  Watched
                                </Text>
                              </Flex>
                            </Flex>
                          </>
                        )}
                      </Flex>
                    </Box> */}
                  </Flex>

                  <Text
                    className="mb-4 mt-2 text-center font-medium text-[#9F9F9F] sm:my-6"
                    fontSize={{ base: 14, sm: 16, md: 18 }}
                    lineHeight={{ base: 1.35, sm: 'normal' }}
                  >
                    Recent Watch Time Earnings (in LYK Coins) from watching
                  </Text>

                  <TableContent type="viewer" />

                  <Flex className="justify-end">
                    <Button
                      className="font-medium no-underline max-sm:w-full"
                      py={5}
                      px={10}
                      fontSize={{ base: 14, sm: 16 }}
                      bg="lyk-green"
                      color={'white'}
                      as={Link}
                      href="/earnings/breakdown"
                      _hover={{ opacity: 0.7, textDecoration: 'none' }}
                      textDecoration={'none'}
                    >
                      View Breakdowns
                    </Button>
                  </Flex>
                </>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </>
  )
}

export default RecentActivity
